import {ClassStep, Course} from "models"
import React, {useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AdminButton from "components/admin/button"
import AdminCheckbox from "components/admin/checkbox"
import AdminInput from "components/admin/input"
import {AdminSelectScoreFactors} from "components/score-factors/select"
import AppHistory from "shared/history"
import Attachment from "components/inputs/attachment"
import Checkboxes from "components/inputs/checkboxes"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import FormDataObjectizer from "form-data-objectizer"
import {incorporate} from "incorporator"
import inflection from "inflection"
import Input from "components/inputs/input"
import Locales from "shared/locales"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import {ScoreFactorGroupsSelectWithContainer} from "components/score-factor-groups/select"
import Select from "components/inputs/select"
import translatedAttributes from "@kaspernj/api-maker/build/translated-attributes.js"
import TranslatedCollections from "@kaspernj/api-maker/build/translated-collections"
import {useAdminLayout} from "components/admin/layout"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

const selectedCourseAttributes = [
  "backgroundImageContentType",
  "backgroundImageUrl",
  "coursePageTheme",
  "highlightedInTop",
  "id",
  "name",
  "public",
  "randomContent",
  "resultAverageScoreFactorGroupId",
  "resultBarChartScoreFactorGroupId",
  "resultDevelopmentScoreFactorGroupId",
  "resultPageContentIdentifier",
  "scoreFactorId",
  "valuePoints"
].concat(translatedAttributes(["description", "estimatedTime", "name"], Locales.availableLocales()))

/** @typedef {object} RoutesAdminCoursesEditProps */

/**
 * @typedef {object} RoutesAdminCoursesEditState
 * @property {unknown} classStepsCollection
 * @property {unknown} form
 */

/**
 * @extends {ShapeComponent<RoutesAdminCoursesEditProps, RoutesAdminCoursesEditState>}
 */
class RoutesAdminCoursesEdit extends ShapeComponent {
  /** @type {RoutesAdminCoursesEditState} */
  state = {
    classStepsCollection: undefined,
    form: undefined
  }

  setup() {
    const {locale, t} = useI18n({namespace: "js.routes.admin.courses.edit"})
    const params = useParams()
    const {course, courseId} = useModel(Course, {
      match: {params},
      newIfNoId: {
        defaults: () => ({
          id: uuidv4()
        })
      },
      preload: ["course_class_steps"],
      select: {
        Course: selectedCourseAttributes,
        CourseClassStep: ["classStepId"]
      }
    })
    const coursePageThemeOptions = useMemo(
      () => [
        {text: t(".course_page_theme_default"), value: ""},
        ...TranslatedCollections.get(Course, "course_page_theme").map(({translation, value}) => ({text: translation, value}))
      ],
      [locale, t]
    )

    this.course = course
    this.courseId = courseId
    this.coursePageThemeOptions = coursePageThemeOptions
    this.formRef = useRef()
    this.t = t


    useMemo(() => {
      this.loadClassSteps()
    }, [])
    useAdminLayout()?.setState({active: "courses", headerTitle: this.headerTitle()})
  }

  async loadClassSteps() {
    const classSteps = await ClassStep
      .ransack({s: "position"})
      .select({ClassStep: ["id", "name"]})
      .toArray()

    this.setState({
      classStepsCollection: classSteps.map((classStep) => [classStep.name(), classStep.id()])
    })
  }

  render() {
    const {course, coursePageThemeOptions, onSubmit, t} = this.tt
    const {classStepsCollection} = this.s

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "admin/courses/edit"}}>
        {course &&
          <Form formRef={this.tt.formRef} onSubmit={onSubmit} setForm={this.setStates.form}>
            {Locales.availableLocales().map((locale) =>
              <View dataSet={this.cache(`translatedInputsDataSet${locale}`, {class: "localized-inputs", locale}, [])} key={`input-name-${locale}`}>
                <AdminInput
                  attribute={`name${inflection.camelize(locale)}`}
                  containerProps={{style: {marginBottom: 15}}}
                  label={`${Course.humanAttributeName("name")} (${Locales.labelForLocale(locale)})`}
                  model={course}
                />
                <Input
                  attribute={`description${inflection.camelize(locale)}`}
                  label={`${Course.humanAttributeName("description")} (${Locales.labelForLocale(locale)})`}
                  model={course}
                  type="ckeditor"
                />
                <AdminInput
                  attribute={`estimatedTime${inflection.camelize(locale)}`}
                  label={`${Course.humanAttributeName("estimatedTime")} (${Locales.labelForLocale(locale)})`}
                  model={course}
                  style={this.estimatedTimeInputStyle ||= {marginVertical: 10}}
                />
              </View>
            )}
            <AdminCheckbox attribute="highlightedInTop" model={course} />
            <View style={this.scoreFactorIdViewStyle ||= {marginBottom: 15}}>
              <AdminSelectScoreFactors attribute="scoreFactorId" label={Course.humanAttributeName("scoreFactor")} model={course} />
            </View>
            <AdminInput attribute="valuePoints" containerProps={this.valuePointsInputStyle ||= {style: {marginBottom: 15}}} model={course} />
            {classStepsCollection &&
              <Checkboxes
                defaultValue={course.isPersisted() ? course.courseClassSteps().map((courseClassStep) => courseClassStep.classStepId()) : null}
                label={ClassStep.modelName().human({count: 2})}
                name="course[class_step_ids]"
                options={classStepsCollection}
              />
            }
            <Attachment attribute="backgroundImage" defaultValue={null} label={t("js.shared.background_image")} model={course} />
            <Select
              attribute="coursePageTheme"
              containerProps={this.coursePageThemeContainerProps ||= {style: {marginBottom: 15}}}
              model={course}
              options={coursePageThemeOptions}
              type="hayaSelect"
            />
            <AdminInput
              attribute="resultPageContentIdentifier"
              containerProps={this.resultPageContentIdentifierInputContainerProps ||= {style: {marginBottom: 15}}}
              model={course}
            />
            <ScoreFactorGroupsSelectWithContainer
              attribute="resultAverageScoreFactorGroupId"
              label={Course.humanAttributeName("resultAverageScoreFactorGroup")}
              model={course}
            />
            <ScoreFactorGroupsSelectWithContainer
              attribute="resultBarChartScoreFactorGroupId"
              label={Course.humanAttributeName("resultBarChartScoreFactorGroup")}
              model={course}
            />
            <ScoreFactorGroupsSelectWithContainer
              attribute="resultDevelopmentScoreFactorGroupId"
              label={Course.humanAttributeName("resultDevelopmentScoreFactorGroup")}
              model={course}
            />
            <AdminCheckbox attribute="public" model={course} />
            <AdminCheckbox attribute="randomContent" model={course} />
            <AdminButton primary save submit />
          </Form>
        }
      </View>
    )
  }

  headerTitle() {
    const {course} = this.tt

    if (course?.isNewRecord()) {
      return this.t(".add_new_course")
    } else if (course?.isPersisted()) {
      return this.t(".edit_course", {name: course.name()})
    }
  }

  onSubmit = async () => {
    const {course, t} = this.tt
    const formData = new FormData(this.tt.formRef.current)
    const formObjectData = this.s.form.asObject()
    const data = incorporate({}, formObjectData, FormDataObjectizer.toObject(formData))
    const backgroundImageInput = this.tt.formRef.current?.querySelector("#course_background_image")
    const hasNewBackgroundImage = Boolean(backgroundImageInput?.files?.length)
    const backgroundImagePurgeValue = data?.course?.background_image_purge ?? data?.course?.backgroundImagePurge
    const purgeBackgroundImage = backgroundImagePurgeValue === true ||
      backgroundImagePurgeValue === 1 ||
      backgroundImagePurgeValue === "1" ||
      backgroundImagePurgeValue === "true"

    // Keep existing background image unless the user explicitly uploads a new one or requests purge.
    if (!hasNewBackgroundImage && !purgeBackgroundImage && data?.course) {
      delete data.course.background_image
      delete data.course.backgroundImage
    }

    this.normalizeOptionalSelectValues(data, formObjectData)

    if (course.isNewRecord()) {
      data.course.new_id = course.id()
    }

    try {
      await course.saveRaw(data)
      FlashMessage.success(t(".the_course_was_saved"))
      AppHistory.push(Routes.adminCoursePath(course.id()))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  normalizeOptionalSelectValues(data, formObjectData) {
    this.normalizeOptionalUuidSelectValue(data, formObjectData, "score_factor_id", "scoreFactorId")
    this.normalizeOptionalUuidSelectValue(data, formObjectData, "result_average_score_factor_group_id", "resultAverageScoreFactorGroupId")
    this.normalizeOptionalUuidSelectValue(data, formObjectData, "result_bar_chart_score_factor_group_id", "resultBarChartScoreFactorGroupId")
    this.normalizeOptionalUuidSelectValue(data, formObjectData, "result_development_score_factor_group_id", "resultDevelopmentScoreFactorGroupId")

    const coursePageTheme = data?.course?.course_page_theme ?? data?.course?.coursePageTheme

    if (coursePageTheme === "") {
      if ("course_page_theme" in data.course) data.course.course_page_theme = null
      if ("coursePageTheme" in data.course) data.course.coursePageTheme = null
    }
  }

  normalizeOptionalUuidSelectValue(data, formObjectData, snakeAttribute, camelAttribute) {
    // FormDataObjectizer can serialize an optional UUID select as an empty string.
    const value = data?.course?.[snakeAttribute] ?? data?.course?.[camelAttribute]

    if (value === "") {
      const previousValue = formObjectData?.course?.[snakeAttribute] ?? formObjectData?.course?.[camelAttribute]
      const normalizedValue = previousValue || null

      if (snakeAttribute in data.course) data.course[snakeAttribute] = normalizedValue
      if (camelAttribute in data.course) data.course[camelAttribute] = normalizedValue
    }
  }
}

export default memo(shapeComponent(RoutesAdminCoursesEdit))
